/**
 * Cookie Settings Modal - 详细设置弹窗
 */

import React, { useState, useEffect } from 'react';
import { useCmp } from '../../stores/CmpContext';
import { ConsentChoices } from '../../util/cmpStorage';
import s from './cookieSettings.module.scss';
import Link from 'next/link';

// Cookie 分类配置
const CATEGORIES = [
  {
    key: 'necessary' as const,
    name: 'Strictly Necessary',
    description:
      'These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and account access. You cannot disable these cookies.',
    required: true,
  },
  {
    key: 'functional' as const,
    name: 'Functional',
    description:
      'These cookies enable enhanced functionality and personalization, such as remembering your preferences, language settings, and login status. Disabling these may affect your user experience.',
    required: false,
  },
  {
    key: 'analytics' as const,
    name: 'Analytics',
    description:
      'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services and user experience.',
    required: false,
  },
  {
    key: 'advertising' as const,
    name: 'Advertising',
    description:
      'These cookies are used to deliver personalized advertisements and track the effectiveness of ad campaigns. They may be set by third-party advertising partners.',
    required: false,
  },
];

export default function CookieSettings() {
  const { showSettings, closeSettings, saveSettings, consent } = useCmp();
  const [localChoices, setLocalChoices] = useState<ConsentChoices>({ ...consent });

  // 同步外部 consent 到本地状态
  useEffect(() => {
    setLocalChoices({ ...consent });
  }, [consent, showSettings]);

  if (!showSettings) return null;

  const handleToggle = (key: keyof ConsentChoices) => {
    if (key === 'necessary') return; // 不可关闭
    setLocalChoices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    saveSettings(localChoices);
  };

  const handleAcceptAll = () => {
    saveSettings({
      necessary: true,
      functional: true,
      analytics: true,
      advertising: true,
    });
  };

  const handleRejectAll = () => {
    saveSettings({
      necessary: true,
      functional: false,
      analytics: false,
      advertising: false,
    });
  };

  return (
    <div className={s.overlay} onClick={closeSettings}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.header}>
          <div className={s.title}>Cookie Settings</div>
          <button type="button" className={s.closeBtn} onClick={closeSettings} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={s.body}>
          <div className={s.intro}>
            We use cookies to enhance your experience. You can customize your cookie preferences
            below. For more information, please read our{' '}
            <Link href="/cookie-policy" target="_blank" className={s.link}>
              Cookie Policy
            </Link>
            .
          </div>

          <div className={s.categories}>
            {CATEGORIES.map((cat) => (
              <div key={cat.key} className={s.category}>
                <div className={s.catHeader}>
                  <div className={s.catName}>{cat.name}</div>
                  <label className={s.switch}>
                    <input
                      type="checkbox"
                      checked={localChoices[cat.key]}
                      onChange={() => handleToggle(cat.key)}
                      disabled={cat.required}
                    />
                    <span className={`${s.slider} ${cat.required ? s.disabled : ''}`} />
                  </label>
                </div>
                <div className={s.catDesc}>{cat.description}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={s.footer}>
          <button type="button" className={s.btnReject} onClick={handleRejectAll}>
            Reject All
          </button>
          <button type="button" className={s.btnAccept} onClick={handleAcceptAll}>
            Accept All
          </button>
          <button type="button" className={s.btnSave} onClick={handleSave}>
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

